import { worldTaskState, type WorldActor, type WorldPlace, type WorldState, type WorldTask } from './living-world';
import { worldTaskBadge } from './world-task-status';
import { recapTaskStatus, type RecapEvent } from './world-recap';
import { effectiveGoalStatus, knownGoalOutcome } from '../goal-status';
import { landmarkStage, worldPins, type LandmarkStage } from './world-missions';

export interface WorldGoal {
  id: string; title: string; brief?: string; status: string; outcome?: string; percent?: number; updatedDate?: string; parentGoalId?: string; requiredForParent?: boolean;
  tasks?: { id: string; title: string; status: string; agentId?: string }[];
}
export interface WorldGoalView extends Omit<WorldGoal, 'tasks'> {
  completed: number; total: number; unknown: number; partial: boolean; achieved: boolean; x: number; z: number; radius: number;
  celebrationKey?: string;
  pinned: boolean; stage: LandmarkStage;
  tasks: { id: string; title: string; agentId: string; state: WorldState; statusKey: string }[];
}

export function worldGoalUpdatedToday(goal: Pick<WorldGoal, 'updatedDate'>, now: number): boolean {
  const updated = Date.parse(goal.updatedDate || '');
  return Number.isFinite(updated) && updated <= now && new Date(updated).toDateString() === new Date(now).toDateString();
}

export function worldGoals(goals: WorldGoal[], tasks: WorldTask[], places: WorldPlace[], history?: { events: RecapEvent[]; index: number }, now = Date.now(), pinnedGoalIds: string[] = []): WorldGoalView[] {
  const pins = worldPins(pinnedGoalIds);
  const observed = new Map<string, WorldState>();
  const completed = new Set<string>();
  if (history) for (const event of history.events.slice(0, history.index + 1)) {
    if (event.snapshot) for (const task of event.snapshot) observed.set(task.taskId, task.state);
    else if (event.taskId) {
      observed.set(event.taskId, event.state);
      if (event.milestone === 'completed') completed.add(event.taskId);
      else if (event.state !== 'done') completed.delete(event.taskId);
    }
  }
  const byTask = new Map(tasks.map(task => [task.id, task]));
  const byGoal = new Map(goals.map(goal => [goal.id, goal]));
  const radius = Math.max(22, ...places.map(place => Math.hypot(place.x, place.z) + 9));
  return goals.filter(goal => !goal.parentGoalId || !byGoal.has(goal.parentGoalId)).sort((a, b) => a.id.localeCompare(b.id)).flatMap(goal => {
    const members = goals.filter(member => member.parentGoalId === goal.id);
    const family = [goal, ...members]; const ids = new Set(family.map(item => item.id));
    const linked = new Map(family.flatMap(item => item.tasks || []).map(task => [task.id, task]));
    for (const task of tasks) if (task.goalId && ids.has(task.goalId)) linked.set(task.id, { ...task, title: task.title || '', status: task.status || '' });
    const currentOutcome = knownGoalOutcome(goal.outcome);
    const currentDone = [...linked.values()].filter(task => (byTask.get(task.id) || task).status === 'COMPLETED').length;
    const currentPercent = goal.percent ?? (!family.some(item => !item.tasks) && linked.size ? currentDone / linked.size * 100 : 0);
    const currentAchieved = currentOutcome ? currentOutcome === 'REACHED' : effectiveGoalStatus(goal.status, currentPercent) === 'ACHIEVED';
    const updatedToday = worldGoalUpdatedToday(goal, now);
    // Recap and Live share the day's project roster; historical task states stay independent.
    const pinned = pins.includes(goal.id);
    if (!pinned && (currentAchieved || currentOutcome || ['ACHIEVED', 'ABANDONED'].includes(goal.status)) && !updatedToday) return [];
    const cursor = history?.events[history.index]?.at;
    const rows = [...linked.values()].filter(row => {
      const created = Date.parse(byTask.get(row.id)?.createdDate || '');
      return !history || !Number.isFinite(created) || cursor === undefined || created <= cursor;
    }).map(row => {
      const task = byTask.get(row.id) || row;
      // Current goal metadata can name a task, but cannot supply its historical status.
      const historicalStatus = history && cursor !== undefined ? recapTaskStatus(task, cursor) : undefined;
      const state = history ? observed.get(row.id) ?? (historicalStatus ? worldTaskState({ id: row.id, status: historicalStatus }) : 'unknown') : worldTaskState(task);
      const actor = { state } as WorldActor;
      if (historicalStatus === 'COMPLETED' && state === 'done') completed.add(row.id);
      return { id: row.id, title: row.title, agentId: task.agentId || row.agentId || '', state,
        statusKey: worldTaskBadge(actor, history ? historicalStatus ? { id: row.id, status: historicalStatus } : undefined : task, !!history && !historicalStatus).labelKey };
    });
    const done = rows.filter(row => row.state === 'done' && (history ? completed.has(row.id) : byTask.get(row.id)?.status === 'COMPLETED' || linked.get(row.id)?.status === 'COMPLETED')).length;
    const partial = !!history || family.some(item => !item.tasks);
    const outcome = history ? undefined : currentOutcome;
    const percent = history ? undefined : goal.percent ?? (!partial && rows.length ? done / rows.length * 100 : 0);
    const achieved = !history && currentAchieved;
    const celebrationKey = achieved && updatedToday ? `${goal.id}:${new Date(goal.updatedDate!).toDateString()}` : undefined;
    if (!rows.length && !(currentAchieved && updatedToday) && !pinned && goal.status !== 'ACTIVE') return [];
    return [{ ...goal, brief: goal.brief || '', status: history ? 'UNKNOWN' : goal.status, outcome, percent, achieved, celebrationKey,
      completed: done, pinned, stage: landmarkStage(achieved, done, percent),
      total: rows.length, unknown: rows.filter(row => row.state === 'unknown').length,
      partial, tasks: rows,
      x: 0, z: 0, radius: 3.3 }];
  }).map((goal, index, all) => {
    const angle = index * Math.PI * 2 / Math.min(8, all.length) - Math.PI / 2 + 0.3;
    const distance = radius + Math.floor(index / 8) * 9;
    return { ...goal, x: Math.cos(angle) * distance, z: Math.sin(angle) * distance };
  });
}
